import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { isEqual } from 'lodash-es';
import { BalanceListRes } from '@/types/api/deposit-payment/balance-list.d';
import { Env } from '@/libs/env';

export interface BalanceData {
  [currency: string]: number;
}

export type SupportedCurrency = 'USDT' | 'INR' | 'PKR' | 'BRL' | 'MXN' | string;

// 存储键配置
export const STORAGE_KEYS = {
  SELECTED_CURRENCY: 'selected_currency',
  CURRENT_CHANNEL: 'current_channel',
  CURRENT_NETWORK: 'current_network',
  CURRENT_PAYMENT: 'current_payment',
  CURRENT_DEPOSIT_CASH: 'current_deposit_cash',
} as const;

// 根据 region 获取默认货币
export const getDefaultCurrency = (): SupportedCurrency => {
  const region = Env.REGION;
  const currencyMap: Record<string, SupportedCurrency> = {
    'india': 'INR',
    'brazil': 'BRL',
    'global': 'USDT',
  };
  return currencyMap[region] || 'USDT';
};

// 使用 atomWithStorage 创建持久化 atoms
export const selectedCurrencyAtom = atomWithStorage<SupportedCurrency>(
  STORAGE_KEYS.SELECTED_CURRENCY,
  getDefaultCurrency()
);

export const currentChannelAtom = atomWithStorage<string>(
  STORAGE_KEYS.CURRENT_CHANNEL,
  ''
);

export const currentNetworkAtom = atomWithStorage<string>(
  STORAGE_KEYS.CURRENT_NETWORK,
  ''
);

export const currentPaymentAtom = atomWithStorage<string>(
  STORAGE_KEYS.CURRENT_PAYMENT,
  ''
);

export const currentDepositCashAtom = atomWithStorage<string>(
  STORAGE_KEYS.CURRENT_DEPOSIT_CASH,
  ''
);

// 按币种存储金额：{ "INR": "200", "BRL": "500" }
export const currentDepositCashAmountAtom = atomWithStorage<Record<string, string>>(
  'current_deposit_cash_amount',
  {}
);

// 按币种存储支付方式：{ "INR": "payment_id_1", "BRL": "payment_id_2" }
export const currentDepositCashPaymentAtom = atomWithStorage<Record<string, string>>(
  'current_deposit_cash_payment',
  {}
);

// 导出操作函数 - 现在可以直接使用 set 函数，不需要手动处理存储
export const switchCurrency = atom(
  null,
  (_, set, value: SupportedCurrency) => {
    set(selectedCurrencyAtom, value);
  }
);

export const setCurrentChannel = atom(
  null,
  (_, set, value: string) => {
    set(currentChannelAtom, value);
  }
);

export const setCurrentNetwork = atom(
  null,
  (_, set, value: string) => {
    set(currentNetworkAtom, value);
  }
);

export const setCurrentPayment = atom(
  null,
  (_, set, value: string) => {
    set(currentPaymentAtom, value);
  }
);

export const setCurrentDepositCash = atom(
  null,
  (_, set, value: string) => {
    set(currentDepositCashAtom, value);
  }
);

export const resetSelectedCurrency = atom(
  null,
  (_, set) => {
    set(selectedCurrencyAtom, getDefaultCurrency());
  }
);

// 余额列表 atom - 带深度比较，避免相同数据导致的重新渲染
export const balanceListAtom = atom<BalanceListRes | null>(null);

// 余额列表写入 atom - 带深度比较检测
export const setBalanceListAtom = atom(
  null,
  (get, set, newValue: BalanceListRes | null) => {
    const currentValue = get(balanceListAtom);

    // 使用 lodash-es 的 isEqual 进行深度比较
    if (!isEqual(currentValue, newValue)) {
      set(balanceListAtom, newValue);
    }
  }
);

// 充值成功通知 atom - 用于触发动效
export interface RechargeNotification {
  currency: string;
  amount: string;
  eventType: number; // 2:充值, 3:提现, 4:代理佣金
  eventStatus: number; // 1:成功
  msgId?: string; // 消息唯一标识
}

export const rechargeNotificationAtom = atom<RechargeNotification | null>(null);

// 全局控制余额轮询的暂停状态（false: 正常轮询，true: 暂停）
export const balancePollingPausedAtom = atom<boolean>(false);
